import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type PageHeaderState = {
  title?: ReactNode;
  actions?: ReactNode;
  isLoading?: boolean;
};

type PageHeaderContextValue = {
  /** False outside the app shell (tests, embedded pages): nothing renders the header there. */
  hasShell: boolean;
  state: PageHeaderState;
  setHeader: (next: PageHeaderState) => void;
  clearHeader: () => void;
};

const PageHeaderContext = createContext<PageHeaderContextValue>({
  hasShell: false,
  state: {},
  setHeader: () => undefined,
  clearHeader: () => undefined,
});

type ProviderProps = {
  children: ReactNode;
};

export const PageHeaderProvider = ({ children }: ProviderProps) => {
  const [state, setState] = useState<PageHeaderState>({});

  const setHeader = useCallback((next: PageHeaderState) => {
    setState(prev => {
      if (
        prev.title === next.title &&
        prev.actions === next.actions &&
        prev.isLoading === next.isLoading
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const clearHeader = useCallback(() => {
    setState(prev => {
      if (!prev.title && !prev.actions && !prev.isLoading) {
        return prev;
      }
      return {};
    });
  }, []);

  const value = useMemo<PageHeaderContextValue>(
    () => ({
      hasShell: true,
      state,
      setHeader,
      clearHeader,
    }),
    [state, setHeader, clearHeader],
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
};

export const usePageHeaderState = () => {
  const { state } = useContext(PageHeaderContext);
  return state;
};

export const usePageHeaderControls = () => {
  const { setHeader, clearHeader, hasShell } = useContext(PageHeaderContext);
  return { setHeader, clearHeader, hasShell };
};
